/**
 * AI-assisted concept creation service.
 *
 * Calls Claude Sonnet via the Anthropic Messages API with tool_use
 * for structured output. Parses natural language descriptions of
 * football concepts into structured assignment data.
 *
 * Uses raw fetch (no Anthropic SDK) for minimal dependencies.
 *
 * @see docs/plans/vocabulary-and-composition/ Phase 10
 */

import { CANONICAL_ROUTE_TYPES } from '@/constants/RouteAliases'
import type { VocabularyEntry } from '@/db/Types'

// ============================================================================
// Types
// ============================================================================

export interface AIParsedConcept {
	name: string
	category: 'route' | 'blocking' | 'motion' | 'protection'
	targetingMode: 'absolute_role' | 'relative_selector'
	defaultSide: string | null
	assignments: AIParsedAssignment[]
	confidence: number
	ambiguities: string[]
}

export interface AIParsedAssignment {
	target: string
	targetType: 'absolute_role' | 'relative_selector'
	routeType: string
	routeDetails?: string
	confidence: number
}

export interface ConceptParseRequest {
	description: string
	vocabulary?: VocabularyEntry[]
	category?: 'route' | 'blocking' | 'motion' | 'protection'
}

export interface ConceptParseResult {
	success: boolean
	concept: AIParsedConcept | null
	error?: string
}

// ============================================================================
// Constants
// ============================================================================

const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages'

// ============================================================================
// Public API
// ============================================================================

/**
 * Parse a natural language concept description into structured data.
 *
 * Sends the description to Claude Sonnet with a football-specific system prompt
 * and forces structured output via tool_use. Returns gracefully on any failure
 * (missing API key, network error, invalid response).
 */
export async function parseConceptDescription(
	request: ConceptParseRequest,
): Promise<ConceptParseResult> {
	const apiKey = process.env.ANTHROPIC_API_KEY
	if (!apiKey) {
		return { success: false, concept: null, error: 'AI parsing not configured' }
	}

	const systemPrompt = buildSystemPrompt(request.vocabulary, request.category)
	const toolSchema = buildToolSchema()

	try {
		const response = await fetch(ANTHROPIC_API_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'x-api-key': apiKey,
				'anthropic-version': '2023-06-01',
			},
			body: JSON.stringify({
				model: 'claude-sonnet-4-20250514',
				max_tokens: 1024,
				system: systemPrompt,
				messages: [
					{ role: 'user', content: request.description },
				],
				tools: [toolSchema],
				tool_choice: { type: 'tool', name: 'create_concept' },
			}),
		})

		if (!response.ok) {
			const errorBody = await response.text()
			console.error('Anthropic API error:', response.status, errorBody)
			return { success: false, concept: null, error: 'AI service temporarily unavailable' }
		}

		const data = await response.json()
		const toolUse = data.content?.find(
			(block: { type: string }) => block.type === 'tool_use',
		)

		if (!toolUse?.input) {
			return { success: false, concept: null, error: 'AI could not parse the description' }
		}

		const concept = toolUse.input as AIParsedConcept
		return { success: true, concept }
	} catch (error) {
		console.error('AI parsing error:', error)
		return { success: false, concept: null, error: 'Failed to connect to AI service' }
	}
}

// ============================================================================
// Prompt Construction
// ============================================================================

/**
 * Build the system prompt for football concept parsing.
 *
 * Includes:
 * - Role and task description
 * - Available targeting modes and route types
 * - Available player selectors
 * - Vocabulary context (if provided)
 * - Few-shot examples for consistent output
 */
export function buildSystemPrompt(
	vocabulary?: VocabularyEntry[],
	category?: 'route' | 'blocking' | 'motion' | 'protection',
): string {
	const routeTypesList = CANONICAL_ROUTE_TYPES.join(', ')

	const categoryInstruction = category
		? `\nThe user has pre-selected category "${category}". Use this as the concept category.`
		: ''

	const vocabularyContext = vocabulary?.length
		? `\n\nExisting playbook vocabulary (codes already in use):\n${vocabulary.map(v => `- "${v.code}" → ${v.category} (${v.display_name ?? v.entity_type})`).join('\n')}`
		: ''

	return `You are a football concept parser. Your job is to convert natural language descriptions of football concepts into structured data using the create_concept tool.

## Targeting Modes
- "absolute_role": Target a specific named position (e.g., "X", "Y", "Z", "H", "F", "QB", "RB", "LT", "LG", "C", "RG", "RT", "TE")
- "relative_selector": Target by numbered receiver alignment (e.g., "#1_receiver", "#2_receiver", "#3_receiver", "#4_receiver", "#5_receiver")

## Available Route Types
${routeTypesList}

## Player Selectors
Absolute roles: X, Y, Z, H, F, QB, RB, FB, LT, LG, C, RG, RT, TE, WR, SE, FL
Relative selectors: #1_receiver, #2_receiver, #3_receiver, #4_receiver, #5_receiver

## Side Options
- null (no side preference, side-agnostic)
- "left", "right" (absolute side)
- "strong", "weak" (relative to formation strength)
${categoryInstruction}${vocabularyContext}

## Rules
1. Infer the category from the description if not pre-selected.
2. Choose absolute_role targeting when specific positions are named (e.g., "X runs a go").
3. Choose relative_selector targeting when descriptions use alignment-based language (e.g., "#1 runs a post").
4. Set confidence between 0 and 1 based on how clear the description is.
5. List any ambiguities that the coach might want to clarify.
6. Use canonical route types from the list above.

## Examples

Input: "Smash concept - outside receiver runs a hitch, inside receiver runs a corner"
Output: name="Smash", category="route", targetingMode="relative_selector", assignments=[
  {target: "#1_receiver", targetType: "relative_selector", routeType: "curl", confidence: 0.9},
  {target: "#2_receiver", targetType: "relative_selector", routeType: "corner", confidence: 0.9}
], confidence=0.9

Input: "X runs a post, Z runs a dig, Y runs a flat"
Output: name="Post-Dig-Flat", category="route", targetingMode="absolute_role", assignments=[
  {target: "X", targetType: "absolute_role", routeType: "post", confidence: 1.0},
  {target: "Z", targetType: "absolute_role", routeType: "in", confidence: 0.9},
  {target: "Y", targetType: "absolute_role", routeType: "flat", confidence: 1.0}
], confidence=0.95

Input: "Power right - RG pulls, FB kicks out the end"
Output: name="Power", category="blocking", targetingMode="absolute_role", defaultSide="right", assignments=[
  {target: "RG", targetType: "absolute_role", routeType: "pull", confidence: 1.0},
  {target: "FB", targetType: "absolute_role", routeType: "kick_out", confidence: 0.95, routeDetails: "kick out the end"}
], confidence=0.95

Input: "Mesh - H and F run shallow crosses in opposite directions"
Output: name="Mesh", category="route", targetingMode="absolute_role", assignments=[
  {target: "H", targetType: "absolute_role", routeType: "shallow_cross", confidence: 0.9, routeDetails: "crossing left to right"},
  {target: "F", targetType: "absolute_role", routeType: "shallow_cross", confidence: 0.9, routeDetails: "crossing right to left"}
], confidence=0.85, ambiguities=["Direction of each crosser depends on alignment"]

Input: "#1 runs a go, #2 runs a post, #3 runs a dig"
Output: name="Vertical Stretch", category="route", targetingMode="relative_selector", assignments=[
  {target: "#1_receiver", targetType: "relative_selector", routeType: "go", confidence: 1.0},
  {target: "#2_receiver", targetType: "relative_selector", routeType: "post", confidence: 1.0},
  {target: "#3_receiver", targetType: "relative_selector", routeType: "in", confidence: 0.9}
], confidence=0.9`
}

/**
 * Build the tool schema for structured concept output.
 *
 * Forces Claude to return data in a predictable shape via tool_use.
 */
export function buildToolSchema() {
	return {
		name: 'create_concept',
		description: 'Create a structured football concept from the description',
		input_schema: {
			type: 'object' as const,
			properties: {
				name: { type: 'string' as const, description: 'Name of the concept' },
				category: {
					type: 'string' as const,
					enum: ['route', 'blocking', 'motion', 'protection'],
				},
				targetingMode: {
					type: 'string' as const,
					enum: ['absolute_role', 'relative_selector'],
				},
				defaultSide: {
					type: ['string', 'null'] as const,
					enum: ['left', 'right', 'strong', 'weak', null],
				},
				assignments: {
					type: 'array' as const,
					items: {
						type: 'object' as const,
						properties: {
							target: {
								type: 'string' as const,
								description: 'Player target: "X", "#1_receiver", etc.',
							},
							targetType: {
								type: 'string' as const,
								enum: ['absolute_role', 'relative_selector'],
							},
							routeType: {
								type: 'string' as const,
								description: 'Canonical route type',
							},
							routeDetails: {
								type: 'string' as const,
								description: 'Additional route details',
							},
							confidence: {
								type: 'number' as const,
								minimum: 0,
								maximum: 1,
							},
						},
						required: ['target', 'targetType', 'routeType', 'confidence'],
					},
				},
				confidence: {
					type: 'number' as const,
					minimum: 0,
					maximum: 1,
				},
				ambiguities: {
					type: 'array' as const,
					items: { type: 'string' as const },
				},
			},
			required: [
				'name', 'category', 'targetingMode', 'defaultSide',
				'assignments', 'confidence', 'ambiguities',
			],
		},
	}
}
